import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { ShoppingCart, User, Menu, X, LogOut } from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <nav className="mx-auto w-full max-w-[100vw] px-3 sm:px-4 md:px-6 py-3 glass rounded-none md:rounded-2xl">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-200/20 to-purple-400/20 flex items-center justify-center ring-1 ring-white/20 shadow-md transition-transform duration-500 hover:scale-105 hover:shadow-yellow-200/40">
              <span className="text-yellow-200 text-xl">✨</span>
            </div>
            <div className="brand text-2xl leading-none glow">Celestine</div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/#rolam" className="hover:text-yellow-200/90 transition">Rólam</Link>
            <Link to="/szolgaltatasok" className="hover:text-yellow-200/90 transition font-medium text-yellow-200">Szolgáltatások</Link>
            <Link to="/termekek" className="hover:text-yellow-200/90 transition font-medium text-yellow-200">Termékek</Link>
            <Link to="/galeria" className="hover:text-yellow-200/90 transition">Galéria</Link>
            <Link to="/blog" className="hover:text-yellow-200/90 transition">Blog & Podcast</Link>
            <Link to="/#kapcsolat" className="hover:text-yellow-200/90 transition">Kapcsolat</Link>
          </div>

          {/* Right Side - Cart, User, CTA */}
          <div className="hidden md:flex items-center gap-4">
            {/* Cart */}
            <Link to="/kosar" className="relative hover:text-yellow-200/90 transition">
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-yellow-200 text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {user ? (
              <div className="flex items-center gap-3">
                <Link to="/profil" className="hover:text-yellow-200/90 transition flex items-center gap-2">
                  <User className="w-5 h-5" />
                  <span>{user.name}</span>
                </Link>
                {user.role === 'admin' && (
                  <Link to="/admin" className="btn-magic rounded-lg px-4 py-2 text-sm">Admin</Link>
                )}
                <button onClick={handleLogout} className="hover:text-yellow-200/90 transition">
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <Link to="/bejelentkezes" className="btn-magic rounded-lg px-4 py-2 text-sm whitespace-nowrap">
                Bejelentkezés
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden relative w-10 h-10 flex items-center justify-center"
          >
            {isMenuOpen ? <X className="w-6 h-6 text-yellow-200" /> : <Menu className="w-6 h-6 text-yellow-200" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 py-4 border-t border-white/10">
            <div className="flex flex-col space-y-3 text-sm">
              <Link to="/#rolam" onClick={() => setIsMenuOpen(false)} className="hover:text-yellow-200/90 transition">Rólam</Link>
              <Link to="/szolgaltatasok" onClick={() => setIsMenuOpen(false)} className="hover:text-yellow-200/90 transition font-medium text-yellow-200">Szolgáltatások</Link>
              <Link to="/termekek" onClick={() => setIsMenuOpen(false)} className="hover:text-yellow-200/90 transition font-medium text-yellow-200">Termékek</Link>
              <Link to="/galeria" onClick={() => setIsMenuOpen(false)} className="hover:text-yellow-200/90 transition">Galéria</Link>
              <Link to="/blog" onClick={() => setIsMenuOpen(false)} className="hover:text-yellow-200/90 transition">Blog & Podcast</Link>
              <Link to="/#kapcsolat" onClick={() => setIsMenuOpen(false)} className="hover:text-yellow-200/90 transition">Kapcsolat</Link>
              
              <div className="border-t border-white/10 pt-3 mt-3">
                <Link to="/kosar" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-2 hover:text-yellow-200/90 transition">
                  <ShoppingCart className="w-5 h-5" />
                  Kosár {cartCount > 0 && `(${cartCount})`}
                </Link>
              </div>

              {user ? (
                <>
                  <Link to="/profil" onClick={() => setIsMenuOpen(false)} className="hover:text-yellow-200/90 transition">
                    Profil
                  </Link>
                  {user.role === 'admin' && (
                    <Link to="/admin" onClick={() => setIsMenuOpen(false)} className="hover:text-yellow-200/90 transition">
                      Admin
                    </Link>
                  )}
                  <button onClick={() => { handleLogout(); setIsMenuOpen(false); }} className="text-left hover:text-yellow-200/90 transition">
                    Kijelentkezés
                  </button>
                </>
              ) : (
                <Link to="/bejelentkezes" onClick={() => setIsMenuOpen(false)} className="btn-magic rounded-lg px-4 py-2 text-center">
                  Bejelentkezés
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .brand {
          font-family: "Great Vibes", cursive;
        }
        .glow {
          text-shadow: 0 0 12px rgba(255, 255, 255, 0.35), 0 0 24px rgba(199, 146, 12, 0.25);
        }
        .btn-magic {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          border: 1px solid hsl(0 0% 100% / 0.14);
        }
        .btn-magic:before {
          content: "";
          position: absolute;
          inset: -200%;
          background: conic-gradient(from 0deg, transparent, hsl(45 86% 62% / 0.25), transparent 30%);
          animation: spin 6s linear infinite;
          z-index: -1;
          filter: blur(12px);
        }
        @keyframes spin {
          to { transform: rotate(1turn); }
        }
      `}</style>
    </header>
  );
};

export default Navbar;